import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Component, default as React } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Accordion, Button, Grid, Header, Modal, Table } from 'semantic-ui-react';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import styled from 'styled-components';
import { UpsertMandateBankDto } from 'ts-types/api.types';
import { ibanValidator, required, zipCityValidator } from 'utils/validatorUtils';

const ScrollableTableContainer = styled.div`
  height: 206px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34, 36, 38, .15)
  }
`;

const SegmentHeader = styled(Header)`
  margin-top: 10px !important;
`;

const buttonStyle = {
  marginBottom: 0,
  marginTop: 10
};

interface Props extends MessageSourceProps {
}

interface State {
  activeIndex: number,
  editBank: boolean,
  editBankIndex?: number,
  editField?: string
  bankCopy?: UpsertMandateBankDto,
  addEditModalOpen: boolean
}

class MandateBankSection extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      activeIndex: -1,
      editBank: false,
      editBankIndex: -1,
      addEditModalOpen: false
    }
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {activeIndex, editBank} = this.state;

    return (
        <Accordion fluid>
          <Accordion.Title
              active={activeIndex === 0}
              index={0}
              onClick={this.toggleAccordion}
          >
            <h3><Icon name="dropdown"/>{getMessage('mandate.banks')}</h3>
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 0}>
            {editBank
                ? this.renderMandateBankUpsertForm()
                : this.renderMandateBankTable()
            }
          </Accordion.Content>
        </Accordion>
    );
  }

  renderMandateBankTable = () => {
    const {getMessage} = this.props;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <ScrollableTableContainer>
                <Table celled selectable size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={2}>{getMessage('mandate.banks.vEsr')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('mandate.banks.description')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('mandate.banks.city')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('mandate.banks.bankAccount')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('actions')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <FieldArray name="banks">
                      {({fields}) =>
                          fields.map((field, index) => {
                            const bank: UpsertMandateBankDto = fields.value[index];
                            return (
                                <Table.Row key={index}>
                                  <Table.Cell width={2}>
                                    {bank.vesr ? "VESR" : ""}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {bank.description}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {bank.city}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {bank.bankAccount}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    <EditActionButton
                                        hoverMessage={getMessage('msgEdit')}
                                        onConfirm={() => this.editBank(index, fields, bank, field)}
                                    />
                                    <DeleteActionButton
                                        hoverMessage={getMessage('msgDelete')}
                                        onConfirm={() => this.deleteBill(fields, index)}
                                    />
                                  </Table.Cell>
                                </Table.Row>
                            )
                          })
                      }
                    </FieldArray>
                  </Table.Body>
                </Table>
              </ScrollableTableContainer>
              <FieldArray name="banks">
                {({fields}) => (
                    <Button style={buttonStyle} compact primary
                            onClick={(e) => this.addBank(e, fields, fields.length!)}>
                      {this.props.getMessage('button.add')}
                    </Button>
                )}
              </FieldArray>
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderMandateBankUpsertForm = () => {
    const {getMessage} = this.props;
    const {addEditModalOpen} = this.state;
    return (
        <FieldArray name="banks">
          {({fields}) => (
              <Modal
                  open={addEditModalOpen}
                  onClose={() => this.cancelEditBill(fields)}
                  closeOnDimmerClick={false}
              >
                <Modal.Header>{getMessage('mandate.banks')}</Modal.Header>
                <FormModalContentContainer>
                  <PraxFormGrid>
                    {this.renderAddressUpsertSegment()}
                    {this.renderDetailInformationUpsertSegment()}
                    {this.renderCharterUpsertSegment()}
                  </PraxFormGrid>
                  <Grid.Row>
                    <FormButtonsRow>
                      <Button type="button" compact onClick={() => this.saveBill(fields)} primary>
                        {getMessage('button.save')}
                      </Button>
                      <Button type="button" compact style={buttonStyle}
                              onClick={() => this.cancelEditBill(fields)}>
                        {getMessage('button.cancel')}
                      </Button>
                    </FormButtonsRow>
                  </Grid.Row>
                </FormModalContentContainer>
              </Modal>
          )}
        </FieldArray>
    )
  };

  renderAddressUpsertSegment = () => {
    const {getMessage} = this.props;
    const {editField} = this.state;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("mandate.banks.address")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}/>
            <Grid.Column width={5}>
              <Field
                  name={`${editField}.vesr`}
                  component={CheckBox}
                  label={`${getMessage("mandate.banks.vEsr")}`}
                  toggle
              />
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name={`${editField}.defaultBank`}
                  component={CheckBox}
                  label={`${getMessage("mandate.banks.defaultBank")}`}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.description")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.description`}
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.city")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.city`}
                  component={Input}
                  validate={zipCityValidator}
                  showError
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.account")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.account`}
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.iban")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.iban`}
                  component={Input}
                  validate={ibanValidator}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderDetailInformationUpsertSegment = () => {
    const {getMessage} = this.props;
    const {editField} = this.state;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <SegmentHeader>
                {getMessage("mandate.banks.detailInformation")}
              </SegmentHeader>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.participant")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.participant`}
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.identification")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.identification`}
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.esrFile")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.esrFile`}
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.participantAccount")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.participantAccount`}
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
            </Grid.Column>
            <Grid.Column width={11}>
              <Field
                  name={`${editField}.esrPlus`}
                  component={CheckBox}
                  label={`${getMessage("mandate.banks.esrPlus")}`}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderCharterUpsertSegment = () => {
    const {getMessage} = this.props;
    const {editField} = this.state;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <SegmentHeader>{getMessage("mandate.banks.charter")}</SegmentHeader>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.banks.bankAccount")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name={`${editField}.bankAccount`}
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  toggleAccordion = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndex, editField} = this.state;

    if (!editField) {
      const newIndex = activeIndex === index ? -1 : index;
      this.setState({activeIndex: newIndex});
    }
  };

  showModal = () => {
    this.setState({
      addEditModalOpen: true
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false
    });
  };

  addBank = (e: any, fields: any, index: number) => {
    e.preventDefault();

    fields.push({});

    this.setState({
      editBank: true,
      editBankIndex: index,
      editField: `banks[${index}]`
    });

    this.showModal();
  };

  editBank = (index: number, fields: any, bank: UpsertMandateBankDto, field: string) => {

    this.setState({
      editBank: true,
      editBankIndex: index,
      editField: field,
      bankCopy: bank
    });

    this.showModal();
  };

  saveBill = (fields: any) => {

    if (fields.valid) {
      this.setState({
        editBank: false,
        editBankIndex: undefined,
        editField: undefined,
        bankCopy: undefined
      });

      this.hideModal();
    }
  };

  cancelEditBill = (fields: any) => {
    const {editBankIndex, bankCopy} = this.state;

    fields.remove(editBankIndex);
    if (bankCopy) {
      fields.insert(editBankIndex, bankCopy);
    }

    this.setState({
      editBank: false,
      editBankIndex: undefined,
      editField: undefined,
      bankCopy: undefined
    });

    this.hideModal();
  };

  deleteBill = (fields: any, index: number) => {
    fields.remove(index);
  };

}

export default withMessages(MandateBankSection);
